import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { StatusLayerWrapper, TagOverride, GameStateLabelWrapper, BroadcastWrapper, TieBreakerWrapper } from './StatusLayer.style';
import getNotableGameLabel from 'bdata-sns-shared/utils/getNotableGameLabel';
import { getGameStateBaseLabel } from 'bdata-sns-shared/utils/getGameStateBaseLabel';
import getGameStateExtendedLabel from '../../utils/getGameStateExtendedLabel';
import overrideGameStateLabel from '../../utils/overrideGameStateLabel';
import LabelTypes from 'bdata-sns-shared/types/LabelTypes';
import fetchImgAsset from 'bdata-sns-shared/mini/utils/fetchImgAsset';
import GameStateTypes from 'bdata-sns-shared/game/types/GameStateTypes';
import MiniSBGameModes from 'bdata-sns-shared/mini/types/MiniSBGameModes';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import GameInfoContainer from '../../containers/GameInfoContainer';
import { getLocalTimezone } from 'bdata-sns-shared/store/selectors/getLocalTimezone';
import getTieBreakerLabel from 'bdata-sns-shared/utils/getTieBreakerLabel';

const StatusLayer = ({
    flags = {},
    dictionary = {},
    mode,
    linescore = {},
    gameDate,
    gameNumber,
    gameState,
    status,
    tz,
    isPlayoff,
    isSuspendedResumptionOnDate,
    assetsBaseUrl,
    broadcasts: { label: broadcastLabel, id: broadcastId } = {},
    isLoading,
    epglist,
    epgContentList,
    nationalBroadcaster,
    className,
    disableGameStateLabel = false,
    awayTeam,
    homeTeam,
    locale
}) => {
    if (isLoading) {
        return (
            <StatusLayerWrapper $mode={mode} className={className}>
                <GameStateLabelWrapper
                    $mode={mode}
                >
                    <Skeleton height='10px' width='30px' margin />
                </GameStateLabelWrapper>
            </StatusLayerWrapper>
        );
    }

    const {
        abstract = {},
        startTimeTBD = false,
        statusCode
    } = status;

    const { currentInning, scheduledInnings, inningState } = linescore;
    const { perfectGame, noHitter } = flags;
    const localTimezone = useSelector(getLocalTimezone);

    const game = {
        gameDate,
        gameNumber
    };

    const {
        isPreview,
        isDoubleHeader,
        isWarmup,
        isFreeGame,
        isPreGameDelay,
        isLive,
        isInstantReplay,
        isManagerChallenge,
        isUmpireReview,
        isInGameDelay,
        isSuspended
    } = abstract;

    const { FULL, SHORT } = LabelTypes;
    const baseLabelParams = {
        gameState,
        dictionary,
        currentInning,
        scheduledInnings,
        inningState,
        labelType: (isWarmup) ? FULL : SHORT,
        isSuspendedResumptionOnDate
    };

    const overrideLabelConfig = {
        perfectGame,
        noHitter,
        mode: mode === MiniSBGameModes.GAMEDAY ? MiniSBGameModes.INFINITY : mode,
        ...abstract
    };

    const extendedLabelConfig = {
        perfectGame,
        noHitter,
        isMiniSB: true,
        ...abstract
    };

    let gameStateBaseLabel;
    const gameStateLabelOverride = overrideGameStateLabel({ ...baseLabelParams, ...overrideLabelConfig });
    const gameStateExtendedLabel = getGameStateExtendedLabel({ ...baseLabelParams, ...extendedLabelConfig });

    if (typeof gameStateLabelOverride !== 'undefined') {
        gameStateBaseLabel = gameStateLabelOverride;
    } else {
        const gameStateBaseLabelConfig = {
            gameDate,
            tz,
            isPreview,
            startTimeTBD,
            game,
            isDoubleHeader,
            localTimezone,
            locale
        };
        gameStateBaseLabel = getGameStateBaseLabel({ ...baseLabelParams, ...gameStateBaseLabelConfig });
    }

    const displayBroadcast = isPlayoff && (gameState === GameStateTypes.PREVIEW || gameState === GameStateTypes.LIVE);

    const broadcastImg = (
        <BroadcastWrapper>
            { fetchImgAsset(`/images/tv_station/light/${broadcastId}.svg`, assetsBaseUrl, { alt: 'tv station logo' }) }
        </BroadcastWrapper>
    );
    const notableGameLabelFull = getNotableGameLabel(flags, dictionary, FULL);
    const notableGameLabelShort = getNotableGameLabel(flags, dictionary, SHORT);
    const hideBaseLabel = (isInGameDelay || isSuspended) && (perfectGame === true || noHitter === true);
    const divider = (gameStateBaseLabel && gameStateExtendedLabel && !hideBaseLabel) ? ' - ' : '';
    const isNotableGame = (perfectGame || noHitter);
    const showGameInfoLayer = (isPreview || isLive) && !isNotableGame && !isPreGameDelay && !isSuspended && !isInGameDelay && !isInstantReplay && !isManagerChallenge && !isUmpireReview;
    let tieBreakerText = '';
    if (statusCode === 'FW' || statusCode === 'OW') {
        tieBreakerText = getTieBreakerLabel({ dictionary, awayTeam, homeTeam, shortLabel: true });
    }
    return (
        <StatusLayerWrapper $mode={mode}>
            { !disableGameStateLabel && (
                <GameStateLabelWrapper $mode={mode} className={SHORT}>
                    { !hideBaseLabel && gameStateBaseLabel }{gameStateExtendedLabel && `${divider}${gameStateExtendedLabel}` }
                </GameStateLabelWrapper>
            )}
            { displayBroadcast && !notableGameLabelFull && broadcastImg }
            { isNotableGame && notableGameLabelFull && (
                <TagOverride
                    $mode={mode}
                    label={ notableGameLabelFull }
                    color={ 'link-blue' }
                    className={FULL}
                    dataAttr={{
                        'data-mlb-test': 'notableGameLabelFull',
                        'data-test-id': 'notableGameLabelFull'
                    }}
                />
            )}
            { isNotableGame && notableGameLabelShort && (
                <TagOverride
                    $mode={mode}
                    label={ notableGameLabelShort }
                    color={ 'link-blue' }
                    className={SHORT}
                    dataAttr={{
                        'data-mlb-test': 'notableGameLabelShort',
                        'data-test-id': 'notableGameLabelShort'
                    }}
                />
            )}
            { !isNotableGame && tieBreakerText && (
                <TieBreakerWrapper $mode={mode}>
                    {tieBreakerText}
                </TieBreakerWrapper>
            )}
            { showGameInfoLayer && (
                <GameInfoContainer
                    epglist={ epglist }
                    epgContentList={ epgContentList }
                    isFreeGame ={ isFreeGame }
                    nationalBroadcaster={ nationalBroadcaster }
                />
            )}
        </StatusLayerWrapper>
    );
};

StatusLayer.propTypes = {
    flags: PropTypes.object,
    dictionary: PropTypes.object,
    linescore: PropTypes.object,
    gameDate: PropTypes.string,
    gameNumber: PropTypes.number,
    gameState: PropTypes.string,
    status: PropTypes.object,
    tz: PropTypes.string,
    isPlayoff: PropTypes.bool,
    assetsBaseUrl: PropTypes.string,
    broadcasts: PropTypes.object,
    mode: PropTypes.oneOf(Object.values(MiniSBGameModes)),
    isSuspendedResumptionOnDate: PropTypes.bool,
    isLoading: PropTypes.bool,
    egglist: PropTypes.array,
    nationalBroadcaster: PropTypes.string,
    showeGameInfoLayerOverride: PropTypes.bool,
    epglist: PropTypes.array,
    epgContentList: PropTypes.array,
    className: PropTypes.string,
    disableGameStateLabel: PropTypes.string
};

export default StatusLayer;
